/**
 * A tasting's communications panel — what Wild Apricot's event emails did, on
 * the event itself: announcement status, the reminder ladder, one-click letters
 * to the people attached to it, and the log of everything sent about it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_INVITED_MEMBER_TYPES,
  EVENT_AUDIENCE_LABELS,
  EVENT_AUDIENCE_PRESETS,
  MEMBER_TYPES,
  MESSAGE_CATEGORIES,
  MESSAGE_CATEGORY_LABELS,
  isInactiveMemberType,
  memberTypeLabel,
  type CommsTemplate,
  type EventAudiencePreset,
  type EventCommsOverview,
  type MemberType,
  type MessageCategory,
  type SendableChannel,
} from '@gwc/shared';
import { commsApi, eventCommsApi } from '../api/comms';
import { tastingsApi } from '../api/tastings';
import type { EventRow } from '../api/models';
import { useAuth } from '../auth/AuthProvider';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { toast } from '../lib/toast';
import { Badge, Button, Dialog, Select } from './ui';
import { MergeFieldChips, insertAtCaret } from './MergeFieldHelp';
import { TemplatePreview } from './TemplateEditor';
import { MESSAGE_STATUS_LABEL, RollupLine } from './DeliveryBadges';

const QUICK_SLUGS = [
  'event-final-details',
  'event-reminder-rsvp',
  'event-reminder-payment',
  'event-reminder-wine',
  'event-waitlist-update',
  'event-change',
  'event-thank-you',
];
/** Sent from their own screens, never as a plain message from here. */
const NOT_HERE = ['event-announcement', 'event-wrapup'];

export function EventComms({ event }: { event: EventRow }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ template?: CommsTemplate } | null>(null);
  const [inviting, setInviting] = useState(false);
  const overview = useQuery({
    queryKey: ['event-comms', event.id],
    queryFn: () => eventCommsApi.overview(event.id),
    refetchInterval: 15_000,
  });
  const templates = useQuery({ queryKey: ['comms', 'templates'], queryFn: () => commsApi.templates() });

  const announce = useMutation({
    mutationFn: () => eventCommsApi.announce(event.id),
    onSuccess: (r) => {
      toast.success(r.alreadySent ? 'The announcement had already gone out.' : 'Announcement sent to the invited members.');
      void qc.invalidateQueries({ queryKey: ['event-comms', event.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not announce'),
  });

  // The payment reminder only makes sense on a tasting that takes payment.
  const quickSlugs = QUICK_SLUGS.filter((s) => s !== 'event-reminder-payment' || event.requiresPayment);
  const quick = (templates.data ?? []).filter((t) => t.scope === 'event' && t.slug && quickSlugs.includes(t.slug));
  const others = (templates.data ?? []).filter(
    (t) => t.scope === 'event' && !(t.slug && (quickSlugs.includes(t.slug) || NOT_HERE.includes(t.slug))),
  );
  const data = overview.data;
  const upcoming = event.status === 'upcoming';
  const invitedTypes: readonly string[] = event.invitedMemberTypes ?? DEFAULT_INVITED_MEMBER_TYPES;

  return (
    <section className="card mt-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm uppercase tracking-wide text-gold-400">Communications</h2>
        <Link to={`/comms?tab=history`} className="text-xs text-cream-300/60 hover:text-gold-200">
          All club messages →
        </Link>
      </div>

      {/* Announcement */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-800 p-3">
        <div>
          <p className="text-sm text-cream-100">Save-the-date announcement</p>
          <p className="text-xs text-cream-300/60">
            {data?.announcement
              ? <>Sent {data.announcement.sentAt ? new Date(data.announcement.sentAt).toLocaleString() : ''} · <RollupLine m={data.announcement} /></>
              : `Not yet sent${data ? ` — it goes to the ${data.counts.invited} invited` : ''}.`}
          </p>
        </div>
        {data?.announcement ? (
          <Link to={`/comms/${data.announcement.id}`} className="btn-quiet px-3 py-1 text-xs">
            Who received it
          </Link>
        ) : upcoming ? (
          <Button className="px-3 py-1 text-xs" loading={announce.isPending} onClick={() => announce.mutate()}>
            Announce to invited members
          </Button>
        ) : null}
      </div>

      {/* Who it's offered to */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-800 p-3">
        <div className="min-w-0">
          <p className="text-sm text-cream-100">Invited member types</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {invitedTypes.map((t) => (
              <Badge key={t} tone="gold">
                {memberTypeLabel(t)}
              </Badge>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-cream-300/45">
            Only these can RSVP, and only they get the announcement and RSVP reminders.
          </p>
        </div>
        {upcoming ? (
          <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => setInviting(true)}>
            Open to more member types
          </Button>
        ) : null}
      </div>

      {/* Reminder ladder */}
      <ReminderLadder event={event} reminders={data?.reminders ?? []} />

      {/* Quick sends */}
      <div>
        <p className="mb-2 text-xs uppercase tracking-wide text-cream-300/50">Write to the people on this tasting</p>
        <div className="flex flex-wrap gap-2">
          {quick.map((t) => (
            <Button key={t.id} variant="ghost" className="px-3 py-1 text-xs" onClick={() => setDialog({ template: t })}>
              {t.name}
            </Button>
          ))}
          {others.length ? (
            <Select
              className="py-1 text-xs"
              value=""
              onChange={(e) => {
                const t = templates.data?.find((x) => x.id === e.target.value);
                if (t) setDialog({ template: t });
              }}
            >
              <option value="">Other template…</option>
              {others.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          ) : null}
          <Button variant="quiet" className="px-3 py-1 text-xs" onClick={() => setDialog({})}>
            Blank message
          </Button>
        </div>
        {data ? (
          <p className="mt-2 text-[11px] text-cream-300/45">
            {data.counts.invited} invited · {data.counts.registered} registered · {data.counts.waitlist} waitlisted ·{' '}
            {data.counts.not_rsvpd} haven't replied
            {event.requiresPayment ? ` · ${data.counts.unpaid} not paid` : ''}
            {data.counts.registered ? ` · ${data.counts.no_wine} without a wine` : ''}
            {data.counts.attended ? ` · ${data.counts.attended} attended` : ''}
          </p>
        ) : null}
      </div>

      {/* Log */}
      <div>
        <p className="mb-2 text-xs uppercase tracking-wide text-cream-300/50">Sent about this tasting</p>
        {!data ? (
          <p className="text-xs text-cream-300/50">Loading…</p>
        ) : data.messages.length === 0 ? (
          <p className="text-xs text-cream-300/50">Nothing yet.</p>
        ) : (
          <ul className="divide-y divide-ink-800/60">
            {data.messages.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="min-w-0">
                  <Link to={`/comms/${m.id}`} className="block truncate text-sm text-cream-100 hover:text-gold-200">
                    {m.title}
                  </Link>
                  <p className="text-[11px] text-cream-300/50">
                    {KIND_LABEL[m.kind] ?? (m.origin === 'system' ? 'Automatic' : 'Leadership')}
                    {m.senderName ? ` · ${m.senderName}` : ''} ·{' '}
                    {m.status === 'scheduled' && m.scheduledFor
                      ? `scheduled ${new Date(m.scheduledFor).toLocaleString()}`
                      : m.sentAt
                        ? new Date(m.sentAt).toLocaleString()
                        : MESSAGE_STATUS_LABEL[m.status] ?? m.status}
                  </p>
                </div>
                <RollupLine m={m} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {inviting ? (
        <InviteDialog
          event={event}
          invited={invitedTypes}
          announced={!!data?.announcement}
          onClose={() => setInviting(false)}
        />
      ) : null}

      {dialog ? (
        <EventSendDialog
          event={event}
          template={dialog.template}
          counts={data?.counts}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}

const KIND_LABEL: Record<string, string> = {
  'event.new': 'Announcement',
  'event.invite': 'Invitation widened',
  'event.reminder': 'RSVP reminder',
  'event.reminder.payment': 'Payment reminder',
  'event.reminder.wine': 'Wine reminder',
  'event.nudge': 'Nudge',
  'tasting.wrapup': 'Wrap-up',
  'tasting.summary': 'Results summary',
  'event.message': 'Leadership',
  'tasting.live': 'Live alert',
  'tasting.recap': 'Recap',
  'rsvp.confirmed': 'RSVP confirmation',
  'rsvp.waitlist': 'Waitlist notice',
  'rsvp.promoted': 'Seat opened',
  'rsvp.cancelled': 'RSVP cancelled',
};

// ------------------------------------------------------------ Reminder ladder

function ReminderLadder({
  event,
  reminders,
}: {
  event: EventRow;
  reminders: EventCommsOverview['reminders'];
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const days = event.reminderDays ?? [];

  const save = useMutation({
    mutationFn: (next: number[]) => tastingsApi.update(event.id, { reminderDays: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['event', event.id] });
      void qc.invalidateQueries({ queryKey: ['event-comms', event.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save reminders'),
  });

  const editable = event.status === 'upcoming';

  function add() {
    const n = Number(draft);
    if (!Number.isInteger(n) || n < 0 || n > 90) return;
    setDraft('');
    save.mutate([...new Set([...days, n])].sort((a, b) => b - a));
  }

  return (
    <div className="rounded-lg border border-ink-800 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-cream-100">Automatic reminders</p>
        <p className="text-[11px] text-cream-300/45">
          On the morning of each day: invited members who haven't replied get the RSVP reminder;
          those registered who haven't {event.requiresPayment ? 'paid or ' : ''}entered their wine get their own.
        </p>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {reminders.length === 0 ? (
          <span className="text-xs text-cream-300/50">None — members only get the announcement.</span>
        ) : (
          reminders.map((r) => (
            <span
              key={r.daysBefore}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink-800 px-2.5 py-1 text-xs text-cream-200"
              title={r.sendOn ? `Sends ${r.sendOn}` : 'Needs an exact date'}
            >
              {r.daysBefore === 0 ? 'Day of' : `${r.daysBefore} day${r.daysBefore === 1 ? '' : 's'} before`}
              {r.status === 'sent' && r.message ? (
                <Link to={`/comms/${r.message.id}`}>
                  <Badge tone="success">sent</Badge>
                </Link>
              ) : r.status === 'missed' ? (
                <Badge tone="warning">passed</Badge>
              ) : (
                <Badge>{r.sendOn ?? 'scheduled'}</Badge>
              )}
              {editable && r.status !== 'sent' ? (
                <button
                  type="button"
                  aria-label="Remove reminder"
                  className="text-cream-300/40 hover:text-wine-300"
                  onClick={() => save.mutate(days.filter((d) => d !== r.daysBefore))}
                >
                  ✕
                </button>
              ) : null}
            </span>
          ))
        )}
        {editable ? (
          <span className="inline-flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={90}
              placeholder="days"
              className="field w-20 py-1 text-xs"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
            />
            <Button variant="quiet" className="px-2 py-1 text-xs" disabled={!draft} loading={save.isPending} onClick={add}>
              Add
            </Button>
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Invite dialog

/**
 * "I only send it to members general, founding members, and members at large…
 * until there's a space open, then I send it in consideration." Widening is one
 * way: a type once invited stays invited, and each new type hears about the
 * tasting once.
 */
function InviteDialog({
  event,
  invited,
  announced,
  onClose,
}: {
  event: EventRow;
  invited: readonly string[];
  announced: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const offerable = MEMBER_TYPES.filter((t) => !invited.includes(t) && !isInactiveMemberType(t));
  const [picked, setPicked] = useState<MemberType[]>([]);
  const [announce, setAnnounce] = useState(true);

  const invite = useMutation({
    mutationFn: () => eventCommsApi.invite(event.id, { memberTypes: picked, announce }),
    onSuccess: (r) => {
      const names = picked.map((t) => memberTypeLabel(t)).join(', ');
      toast.success(
        r.messageIds.length
          ? `Opened to ${names}, and the announcement is on its way to them.`
          : `Opened to ${names}.`,
      );
      void qc.invalidateQueries({ queryKey: ['event', event.id] });
      void qc.invalidateQueries({ queryKey: ['event-comms', event.id] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not update the invitation'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="Open to more member types"
      description="The people you add can RSVP from now on, and are included in the RSVP reminders."
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!picked.length} loading={invite.isPending} onClick={() => invite.mutate()}>
            {picked.length ? `Open to ${picked.length} more type${picked.length === 1 ? '' : 's'}` : 'Open'}
          </Button>
        </>
      }
    >
      {offerable.length === 0 ? (
        <p className="text-sm text-cream-300/70">Every active member type is already invited.</p>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            {offerable.map((t) => (
              <label key={t} className="flex cursor-pointer items-center gap-3 text-sm text-cream-100">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-gold-500"
                  checked={picked.includes(t)}
                  onChange={() =>
                    setPicked((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))
                  }
                />
                {memberTypeLabel(t)}
              </label>
            ))}
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-ink-800 p-3 text-sm text-cream-200">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-gold-500"
              checked={announce}
              onChange={(e) => setAnnounce(e.target.checked)}
            />
            <span>
              Send them the announcement
              <span className="block text-xs text-cream-300/55">
                {announced
                  ? 'Each new type gets the save-the-date once, by push and email.'
                  : "The announcement hasn't gone out yet; they'll be included when it does."}
              </span>
            </span>
          </label>
          <p className="text-[11px] text-cream-300/45">Past members and those in memoriam are never invited.</p>
        </div>
      )}
    </Dialog>
  );
}

// --------------------------------------------------------------- Send dialog

function EventSendDialog({
  event,
  template,
  counts,
  onClose,
}: {
  event: EventRow;
  template?: CommsTemplate;
  counts?: EventCommsOverview['counts'];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [title, setTitle] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? 'Dear {{firstName}},\n\n');
  const [audience, setAudience] = useState<EventAudiencePreset>(template?.eventAudience ?? 'registered');
  const [category, setCategory] = useState<MessageCategory>((template?.category as MessageCategory) ?? 'eventReminders');
  const [channels, setChannels] = useState<SendableChannel[]>(template?.channels ?? ['push', 'email']);
  const [scheduledFor, setScheduledFor] = useState('');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [lastFocus, setLastFocus] = useState<'subject' | 'body'>('body');

  useEffect(() => {
    if (!template) return;
    setTitle(template.subject);
    setBody(template.body);
    if (template.eventAudience) setAudience(template.eventAudience);
    setCategory(template.category as MessageCategory);
    setChannels(template.channels);
  }, [template]);

  const debounced = useDebouncedValue(JSON.stringify({ title, body }), 400);
  const preview = useQuery({
    queryKey: ['comms', 'text-preview', event.id, debounced],
    queryFn: () => commsApi.previewText(title, body, event.id),
    enabled: !!(title || body),
  });
  const previewVars = useMemo(
    () => ({
      firstName: user?.firstName ?? 'James',
      lastName: user?.lastName ?? 'Whitmore',
      fullName: `${user?.firstName ?? 'James'} ${user?.lastName ?? 'Whitmore'}`,
    }),
    [user],
  );

  const input = (test: boolean) => ({
    templateId: template?.id,
    title: title.trim(),
    body: body.trim(),
    audience,
    category,
    channels,
    test,
    ...(scheduledFor && !test ? { scheduledFor: new Date(scheduledFor).toISOString() } : {}),
  });

  const send = useMutation({
    mutationFn: () => eventCommsApi.send(event.id, input(false)),
    onSuccess: (r) => {
      toast.success(
        r.status === 'scheduled'
          ? `Scheduled for ${new Date(scheduledFor).toLocaleString()}.`
          : `Sent to ${r.recipientCount} member${r.recipientCount === 1 ? '' : 's'}.`,
      );
      void qc.invalidateQueries({ queryKey: ['event-comms', event.id] });
      void qc.invalidateQueries({ queryKey: ['comms', 'history'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not send'),
  });
  const test = useMutation({
    mutationFn: () => eventCommsApi.send(event.id, input(true)),
    onSuccess: () => toast.success('Test sent to you. Check your inbox.'),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not send the test'),
  });

  const count = counts?.[audience];
  const valid = title.trim() && body.trim();
  function insert(token: string) {
    if (lastFocus === 'subject') setTitle((t) => insertAtCaret(subjectRef.current, token, t));
    else setBody((b) => insertAtCaret(bodyRef.current, token, b));
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={template ? template.name : 'Message about this tasting'}
      description={`About ${event.title}. Tasting details fill in automatically; each member sees their own name.`}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <Button variant="quiet" disabled={!valid} loading={test.isPending} onClick={() => test.mutate()}>
            Send a test to myself
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!valid} loading={send.isPending} onClick={() => send.mutate()}>
              {scheduledFor ? 'Schedule' : `Send to ${count ?? '…'}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[1fr_260px]">
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Send to"
              value={audience}
              onChange={(e) => setAudience(e.target.value as EventAudiencePreset)}
              hint={count != null ? `${count} member${count === 1 ? '' : 's'} right now — resolved again at send time.` : undefined}
            >
              {EVENT_AUDIENCE_PRESETS.map((p) => (
                <option key={p} value={p}>
                  {EVENT_AUDIENCE_LABELS[p]}
                  {counts ? ` (${counts[p]})` : ''}
                </option>
              ))}
            </Select>
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value as MessageCategory)}>
              {MESSAGE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {MESSAGE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-cream-200" htmlFor="ev-subject">
              Subject
            </label>
            <input
              id="ev-subject"
              ref={subjectRef}
              className="field"
              maxLength={140}
              value={title}
              onFocus={() => setLastFocus('subject')}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-cream-200" htmlFor="ev-body">
              Message
            </label>
            <textarea
              id="ev-body"
              ref={bodyRef}
              className="field min-h-[200px]"
              maxLength={5000}
              value={body}
              onFocus={() => setLastFocus('body')}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <MergeFieldChips onInsert={insert} eventAttached />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className="mb-1 block text-sm text-cream-200">Channels</span>
              <div className="flex gap-4 pt-2">
                {(['push', 'email'] as const).map((c) => (
                  <label key={c} className="flex cursor-pointer items-center gap-2 text-sm text-cream-200">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-gold-500"
                      checked={channels.includes(c)}
                      onChange={() =>
                        setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))
                      }
                    />
                    {c === 'push' ? 'Push' : 'Email'}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm text-cream-200" htmlFor="ev-when">
                Schedule (optional)
              </label>
              <input
                id="ev-when"
                type="datetime-local"
                className="field"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </div>
          </div>
        </div>
        <TemplatePreview
          label="How it will read"
          subject={preview.data?.title ?? title}
          body={preview.data?.body ?? body}
          vars={previewVars}
        />
      </div>
    </Dialog>
  );
}
